import { supabase } from '../lib/supabase';

/**
 * Pickup Task Service - Handles pickup/collection scheduling and management
 * Manages driver routes and client pickups
 */

export const pickupTaskService = {
  /**
   * Get all pickup tasks with optional filters
   * @param {Object} filters - Optional filters { date, driverId, zone, status }
   * @returns {Promise<Array>} List of pickup tasks
   */
  async getAll(filters = {}) {
    try {
      let query = supabase
        ?.from('pickup_tasks')
        ?.select(`
          *,
          driver:employees!pickup_tasks_driver_id_fkey(id, name, phone),
          shipment:shipments!pickup_tasks_shipment_id_fkey(id, reference, photos_urls, photo_url)
        `)
        ?.order('date', { ascending: true })
        ?.order('time_window', { ascending: true });

      // Apply filters
      if (filters?.date) {
        query = query?.eq('date', filters?.date);
      }

      if (filters?.driverId) {
        query = query?.eq('driver_id', filters?.driverId);
      }

      if (filters?.zone) {
        query = query?.eq('client_zone', filters?.zone);
      }

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.shipmentId) {
        query = query?.eq('shipment_id', filters?.shipmentId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase - NEVER parse notes column
      return data?.map(row => ({
        id: row?.id,
        date: row?.date,
        timeWindow: row?.time_window,
        clientName: row?.client_name,
        clientPhone: row?.client_phone,
        clientAddress: row?.client_address,
        clientCity: row?.client_city,
        clientZone: row?.client_zone,
        shipmentId: row?.shipment_id,
        containerId: row?.container_id || null,
        driverId: row?.driver_id,
        status: row?.status,
        notes: row?.notes, // ✅ Plain text only - no JSON parsing
        clientSignatureUrl: row?.client_signature_url,
        pickupPhotoUrls: row?.pickup_photo_urls || null, // ✅ ADD: Fetch pickup_photo_urls from pickup_tasks
        createdAt: row?.created_at,
        updatedAt: row?.updated_at,
        driver: row?.driver ? {
          id: row?.driver?.id,
          name: row?.driver?.name,
          phone: row?.driver?.phone
        } : null,
        shipment: row?.shipment ? {
          id: row?.shipment?.id,
          reference: row?.shipment?.reference,
          photosUrls: row?.shipment?.photos_urls || [],
          photoUrl: row?.shipment?.photo_url || null
        } : null
      }));
    } catch (error) {
      console.error('Error fetching pickup tasks:', error);
      throw error;
    }
  },

  /**
   * Get pickup task by ID with shipment details
   * @param {string} pickupId - Pickup task ID
   * @returns {Promise<Object>} Pickup task with shipment details
   */
  async getById(pickupId) {
    const { data, error } = await supabase
      ?.from('pickup_tasks')
      ?.select(`
        *,
        driver:employees(id, name),
        shipment:shipments(id, reference, photos_urls)
      `)
      ?.eq('id', pickupId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      date: data?.date,
      timeWindow: data?.time_window,
      clientName: data?.client_name,
      clientPhone: data?.client_phone,
      clientAddress: data?.client_address,
      clientCity: data?.client_city,
      clientZone: data?.client_zone,
      status: data?.status,
      notes: data?.notes,
      shipmentId: data?.shipment_id,
      driverId: data?.driver_id,
      driver: data?.driver,
      shipment: data?.shipment ? {
        id: data?.shipment?.id,
        reference: data?.shipment?.reference,
        photosUrls: data?.shipment?.photos_urls || []
      } : null,
      pickupPhotoUrls: data?.pickup_photo_urls || [], // ✅ NEW: Photos from pickup_tasks
      clientSignatureUrl: data?.client_signature_url,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Get pickup tasks by shipment ID
   * @param {string} shipmentId - Shipment UUID
   * @returns {Promise<Array>} List of pickup tasks for this shipment
   */
  async getByShipment(shipmentId) {
    try {
      const { data, error } = await supabase
        ?.from('pickup_tasks')
        ?.select(`
          *,
          driver:employees!pickup_tasks_driver_id_fkey(id, name, phone, zone)
        `)
        ?.eq('shipment_id', shipmentId)
        ?.order('date', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        date: row?.date,
        timeWindow: row?.time_window,
        clientName: row?.client_name,
        clientPhone: row?.client_phone,
        clientAddress: row?.client_address,
        clientCity: row?.client_city,
        clientZone: row?.client_zone,
        shipmentId: row?.shipment_id,
        driverId: row?.driver_id,
        status: row?.status,
        notes: row?.notes,
        clientSignatureUrl: row?.client_signature_url,
        pickupPhotoUrls: row?.pickup_photo_urls || [],
        createdAt: row?.created_at,
        updatedAt: row?.updated_at,
        driver: row?.driver ? {
          id: row?.driver?.id,
          name: row?.driver?.name,
          phone: row?.driver?.phone,
          zone: row?.driver?.zone
        } : null
      }));
    } catch (error) {
      console.error('Error fetching pickup tasks by shipment:', error);
      throw error;
    }
  },

  /**
   * Create new pickup task
   * @param {Object} taskData - Pickup task data in camelCase
   * @returns {Promise<Object>} Created pickup task
   */
  async create(taskData) {
    try {
      const { data, error } = await supabase
        ?.from('pickup_tasks')
        ?.insert({
          date: taskData?.date,
          time_window: taskData?.timeWindow || null,
          client_name: taskData?.clientName,
          client_phone: taskData?.clientPhone || null,
          client_address: taskData?.clientAddress || null,
          client_city: taskData?.clientCity || null,
          client_zone: taskData?.clientZone || null,
          shipment_id: taskData?.shipmentId || null,
          container_id: taskData?.containerId || null,
          driver_id: taskData?.driverId || null,
          status: taskData?.status || 'planifie',
          notes: taskData?.notes || null
        })
        ?.select(`
          *,
          driver:employees(id, name, zone)
        `)
        ?.single();

      if (error) throw error;

      // Convert to camelCase
      return {
        id: data?.id,
        date: data?.date,
        timeWindow: data?.time_window,
        clientName: data?.client_name,
        clientPhone: data?.client_phone,
        clientAddress: data?.client_address,
        clientCity: data?.client_city,
        clientZone: data?.client_zone,
        shipmentId: data?.shipment_id,
        driverId: data?.driver_id,
        status: data?.status,
        notes: data?.notes,
        clientSignatureUrl: data?.client_signature_url,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        driver: data?.driver ? {
          id: data?.driver?.id,
          name: data?.driver?.name,
          zone: data?.driver?.zone
        } : null
      };
    } catch (error) {
      console.error('Error creating pickup task:', error);
      throw error;
    }
  },

  /**
   * Update pickup task
   * @param {string} taskId - Pickup task UUID
   * @param {Object} updates - Fields to update (camelCase)
   * @returns {Promise<Object>} Updated pickup task
   */
  async update(taskId, updates) {
    try {
      // Convert camelCase to snake_case
      const dbUpdates = {};
      if (updates?.date !== undefined) dbUpdates.date = updates?.date;
      if (updates?.timeWindow !== undefined) dbUpdates.time_window = updates?.timeWindow || null;
      if (updates?.clientName !== undefined) dbUpdates.client_name = updates?.clientName;
      if (updates?.clientPhone !== undefined) dbUpdates.client_phone = updates?.clientPhone || null;
      if (updates?.clientAddress !== undefined) dbUpdates.client_address = updates?.clientAddress || null;
      if (updates?.clientCity !== undefined) dbUpdates.client_city = updates?.clientCity || null;
      if (updates?.clientZone !== undefined) dbUpdates.client_zone = updates?.clientZone || null;
      if (updates?.shipmentId !== undefined) dbUpdates.shipment_id = updates?.shipmentId || null;
      if (updates?.containerId !== undefined) dbUpdates.container_id = updates?.containerId || null;
      if (updates?.driverId !== undefined) dbUpdates.driver_id = updates?.driverId || null;
      if (updates?.status !== undefined) dbUpdates.status = updates?.status;
      if (updates?.notes !== undefined) dbUpdates.notes = updates?.notes || null;
      if (updates?.clientSignatureUrl !== undefined) dbUpdates.client_signature_url = updates?.clientSignatureUrl || null;

      const { data, error } = await supabase
        ?.from('pickup_tasks')
        ?.update(dbUpdates)
        ?.eq('id', taskId)
        ?.select(`
          *,
          driver:employees(id, name, zone)
        `)
        ?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        date: data?.date,
        timeWindow: data?.time_window,
        clientName: data?.client_name,
        clientPhone: data?.client_phone,
        clientAddress: data?.client_address,
        clientCity: data?.client_city,
        clientZone: data?.client_zone,
        shipmentId: data?.shipment_id,
        driverId: data?.driver_id,
        status: data?.status,
        notes: data?.notes,
        clientSignatureUrl: data?.client_signature_url,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        driver: data?.driver ? {
          id: data?.driver?.id,
          name: data?.driver?.name,
          zone: data?.driver?.zone
        } : null
      };
    } catch (error) {
      console.error('Error updating pickup task:', error);
      throw error;
    }
  },

  /**
   * Delete pickup task
   * @param {string} taskId - Pickup task UUID
   * @returns {Promise<void>}
   */
  async delete(taskId) {
    try {
      const { error } = await supabase
        ?.from('pickup_tasks')
        ?.delete()
        ?.eq('id', taskId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting pickup task:', error);
      throw error;
    }
  },

  /**
   * Upload client signature image
   * @param {string} taskId - Pickup task UUID
   * @param {Blob} signatureBlob - Signature image blob
   * @returns {Promise<string>} Signature URL
   */
  async uploadSignature(taskId, signatureBlob) {
    try {
      const fileName = `${taskId}-${Date.now()}.png`;
      const filePath = `signatures/${fileName}`;

      // Upload signature to storage
      const { error: uploadError } = await supabase
        ?.storage
        ?.from('signatures')
        ?.upload(filePath, signatureBlob, {
          contentType: 'image/png',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get signed URL (valid for 1 year)
      const { data: urlData, error: urlError } = await supabase
        ?.storage
        ?.from('signatures')
        ?.createSignedUrl(filePath, 31536000); // 1 year in seconds

      if (urlError) throw urlError;

      // Update pickup task with signature URL
      await this.update(taskId, {
        clientSignatureUrl: urlData?.signedUrl,
        status: 'termine'
      });

      return urlData?.signedUrl;
    } catch (error) {
      console.error('Error uploading signature:', error);
      throw error;
    }
  },

  /**
   * Get driver route sheet (tasks for specific driver and date)
   * @param {string} driverId - Driver UUID
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise<Array>} List of tasks for driver's route
   */
  async getDriverRouteSheet(driverId, date) {
    try {
      const { data, error } = await supabase
        ?.from('pickup_tasks')
        ?.select(`
          *,
          shipment:shipments(id, reference)
        `)
        ?.eq('driver_id', driverId)
        ?.eq('date', date)
        ?.order('time_window', { ascending: true });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        timeWindow: row?.time_window,
        clientName: row?.client_name,
        clientPhone: row?.client_phone,
        clientAddress: row?.client_address,
        clientCity: row?.client_city,
        clientZone: row?.client_zone,
        status: row?.status,
        notes: row?.notes, // ✅ Plain text only
        clientSignatureUrl: row?.client_signature_url,
        shipment: row?.shipment ? {
          id: row?.shipment?.id,
          reference: row?.shipment?.reference
        } : null
      }));
    } catch (error) {
      console.error('Error fetching driver route sheet:', error);
      throw error;
    }
  }
};